import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { FileText, Download, Search, TrendingUp, Clock, PoundSterling, ChevronRight, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { listQuotes, getQuotePdfUrl, exportUrl, deleteQuote } from "@/lib/api";
import { formatCurrency, flexMatch } from "@/lib/calculations";
import { useIsMobile } from "@/components/custom/AppShell";

export default function Quotes() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [clientFilter, setClientFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  useEffect(() => {
    listQuotes().then(data => {
      // Only quotes that have been sent (quote_started + have a quote_number)
      const sent = data.filter(q => q.quote_started && q.quote_number);
      // Use live data as source of truth, archive for persistence
      const liveIds = new Set(sent.map(q => q.id));
      const existing = JSON.parse(localStorage.getItem("quotes_archive") || "[]").filter(e => liveIds.has(e.id));
      const merged = [...existing];
      const existingIds = new Set(merged.map(q => q.id));
      sent.forEach(q => {
        if (!existingIds.has(q.id)) {
          merged.push({ id: q.id, quote_number: q.quote_number, site_address: q.site_address, client_type: q.client_type, client_first_name: q.client_first_name, client_last_name: q.client_last_name, quote_date: q.quote_date, status: q.status, total_inc_vat: q.total_inc_vat, total_ex_vat: q.total_ex_vat, vat_amount: q.vat_amount, labour_total: q.labour_total, works_type: q.works_type, correspondence_email: q.correspondence_email });
        } else {
          const idx = merged.findIndex(e => e.id === q.id);
          if (idx >= 0) merged[idx] = { ...merged[idx], status: q.status, total_inc_vat: q.total_inc_vat, total_ex_vat: q.total_ex_vat, vat_amount: q.vat_amount };
        }
      });
      localStorage.setItem("quotes_archive", JSON.stringify(merged));
      setQuotes(merged);
    }).catch(() => toast.error("Failed to load"))
    .finally(() => setLoading(false));
  }, []);

  const handleDeleteQuote = async (e, qId, qNum) => {
    e.stopPropagation();
    if (!window.confirm(`Permanently delete quote ${qNum}? This cannot be undone.`)) return;
    try {
      await deleteQuote(qId);
      setQuotes(prev => prev.filter(q => q.id !== qId));
      const archive = JSON.parse(localStorage.getItem("quotes_archive") || "[]").filter(q => q.id !== qId);
      localStorage.setItem("quotes_archive", JSON.stringify(archive));
      toast.success(`Quote ${qNum} deleted`);
    } catch { toast.error("Failed to delete"); }
  };

  const filtered = useMemo(() => {
    let result = quotes;
    if (search) {
      result = result.filter(q => {
        const combined = [q.site_address, q.quote_number, q.client_first_name, q.client_last_name, q.client_type].filter(Boolean).join(" ");
        return flexMatch(combined, search);
      });
    }
    if (statusFilter) result = result.filter(q => q.status === statusFilter);
    if (clientFilter) result = result.filter(q => q.client_type === clientFilter);
    if (dateFrom || dateTo) {
      result = result.filter(q => {
        if (!q.quote_date) return false;
        const parts = q.quote_date.split("/");
        if (parts.length < 3) return false;
        const d = new Date(parseInt(parts[2]), parseInt(parts[1]) - 1, parseInt(parts[0]));
        if (dateFrom && d < new Date(dateFrom)) return false;
        if (dateTo) { const to = new Date(dateTo); to.setHours(23,59,59); if (d > to) return false; }
        return true;
      });
    }
    return result;
  }, [quotes, search, statusFilter, clientFilter, dateFrom, dateTo]);

  const sorted = useMemo(() => [...filtered].sort((a, b) => {
    const numA = parseInt((a.quote_number || "").replace(/\D/g, "")) || 0;
    const numB = parseInt((b.quote_number || "").replace(/\D/g, "")) || 0;
    return numB - numA;
  }), [filtered]);

  // Stats
  const stats = useMemo(() => {
    const total = quotes.length;
    const totalValue = quotes.reduce((s, q) => s + (q.total_inc_vat || 0), 0);
    // Conversion: quotes sent (reached Quote Sent or beyond) vs quotes accepted (moved past Quote Sent)
    const sentStatuses = ["Quote Sent", "Quote Accepted", "Job Booked", "Job Signed", "Invoiced", "Recall", "Recall Booked", "Reschedule"];
    const acceptedStatuses = ["Quote Accepted", "Job Booked", "Job Signed", "Invoiced", "Recall", "Recall Booked", "Reschedule"];
    const sent = quotes.filter(q => sentStatuses.includes(q.status)).length;
    const accepted = quotes.filter(q => acceptedStatuses.includes(q.status)).length;
    const conversionRate = sent > 0 ? Math.round((accepted / sent) * 100) : 0;
    const avgValue = total > 0 ? totalValue / total : 0;
    const thisMonth = quotes.filter(q => {
      if (!q.quote_date) return false;
      const parts = q.quote_date.split("/");
      if (parts.length < 3) return false;
      const now = new Date();
      return parseInt(parts[1]) === now.getMonth() + 1 && parseInt(parts[2]) === now.getFullYear();
    }).length;
    return { total, totalValue, sent, accepted, conversionRate, avgValue, thisMonth };
  }, [quotes]);

  return (
    <div className="min-h-screen" style={{ background: "var(--bg)" }}>
      <div className={isMobile ? "px-4 pt-4 pb-3" : "px-8 pt-8 pb-4"}>
        <div className="flex items-center justify-between mb-6">
          <h1 className={isMobile ? "text-lg font-semibold" : "ui-page-title"}>Quotes</h1>
          {!isMobile && (
            <a href={exportUrl("quotes")} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-colors" style={{ background: "var(--primary-light)", color: "var(--primary)" }} data-testid="export-quotes">
              <Download className="w-4 h-4" /> Export CSV
            </a>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <div className="ui-surface p-3 md:p-4">
            <p className="ui-label text-[10px] md:text-xs">Total Quotes</p>
            <p className="text-lg md:text-2xl font-mono font-bold mt-1">{stats.total}</p>
          </div>
          <div className="ui-surface p-3 md:p-4">
            <p className="ui-label text-[10px] md:text-xs">Total Value</p>
            <p className="text-lg md:text-2xl font-mono font-bold mt-1" style={{ color: "var(--success)" }}>{formatCurrency(stats.totalValue)}</p>
          </div>
          <div className="ui-surface p-3 md:p-4">
            <p className="ui-label text-[10px] md:text-xs flex items-center gap-1"><TrendingUp className="w-3 h-3" /> Conversion</p>
            <p className="text-lg md:text-2xl font-mono font-bold mt-1">{stats.conversionRate}%</p>
            <p className="text-xs mt-0.5" style={{ color: "var(--text-muted)" }}>{stats.accepted} accepted of {stats.sent} sent</p>
          </div>
          <div className="ui-surface p-3 md:p-4">
            <p className="ui-label text-[10px] md:text-xs">Avg Value</p>
            <p className="text-lg md:text-2xl font-mono font-bold mt-1">{formatCurrency(stats.avgValue)}</p>
          </div>
        </div>

        {/* Search */}
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: "var(--text-muted)" }} />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search quotes..."
            className="pl-9 h-10 rounded-lg"
            style={{ border: "1px solid var(--border)" }}
          />
        </div>

        {/* Filter bar */}
        {!isMobile && (
          <div className="flex items-center gap-3 mb-4 px-4 py-3 rounded-lg" style={{ background: "#F9FAFB", borderBottom: "1px solid var(--border)" }}>
            <Select value={statusFilter} onValueChange={v => setStatusFilter(v === "all" ? "" : v)}>
              <SelectTrigger className="w-44 h-9 rounded-md text-sm" style={{ border: "1px solid var(--border)", background: "var(--surface)" }}><SelectValue placeholder="All Statuses" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="Not Started">Not Started</SelectItem>
                <SelectItem value="Quote Sent">Quote Sent</SelectItem>
                <SelectItem value="Quote Accepted">Quote Accepted</SelectItem>
                <SelectItem value="Job Booked">Job Booked</SelectItem>
                <SelectItem value="Job Signed">Job Signed</SelectItem>
                <SelectItem value="Invoiced">Invoiced</SelectItem>
                <SelectItem value="Awaiting Quote">Awaiting Quote</SelectItem>
              </SelectContent>
            </Select>
            <Select value={clientFilter} onValueChange={v => setClientFilter(v === "all" ? "" : v)}>
              <SelectTrigger className="w-40 h-9 rounded-md text-sm" style={{ border: "1px solid var(--border)", background: "var(--surface)" }}><SelectValue placeholder="All Clients" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Clients</SelectItem>
                {[...new Set(quotes.map(q => q.client_type).filter(Boolean))].sort().map(ct => (
                  <SelectItem key={ct} value={ct}>{ct}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="w-40 h-9 text-sm" style={{ border: "1px solid var(--border)", borderRadius: "6px", padding: "8px 12px" }} />
            <span className="text-xs" style={{ color: "var(--text-muted)" }}>to</span>
            <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="w-40 h-9 text-sm" style={{ border: "1px solid var(--border)", borderRadius: "6px", padding: "8px 12px" }} />
            {(statusFilter || clientFilter || dateFrom || dateTo) && (
              <button onClick={() => { setStatusFilter(""); setClientFilter(""); setDateFrom(""); setDateTo(""); }} className="text-xs font-semibold ml-auto" style={{ color: "var(--primary)" }}>Clear filters</button>
            )}
          </div>
        )}
      </div>

      <div className={isMobile ? "px-4 pb-4" : "px-8 pb-8"}>
        {loading ? (
          <p className="text-center py-10" style={{ color: "var(--text-muted)" }}>Loading...</p>
        ) : sorted.length === 0 ? (
          <p className="text-center py-10" style={{ color: "var(--text-muted)" }}>No quotes found</p>
        ) : isMobile ? (
          <div className="space-y-2">
            {sorted.map(q => (
              <div key={q.id} className="ui-surface p-3 active:scale-[0.99] transition-transform" onClick={() => navigate(`/quotes/${q.id}`)} data-testid={`mobile-quote-${q.quote_number}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-mono font-bold" style={{ color: "var(--primary)" }}>{q.quote_number}</span>
                      <span className={`badge-pill text-[10px] status-${(q.status || "not-started").toLowerCase().replace(/\s+/g, "-")}`}>{q.status}</span>
                    </div>
                    <p className="text-sm font-medium truncate" style={{ color: "var(--text-primary)" }}>{q.site_address || "—"}</p>
                    <p className="text-xs mt-0.5" style={{ color: "var(--text-muted)" }}>
                      {[q.client_first_name, q.client_last_name].filter(Boolean).join(" ")} · {q.client_type}
                    </p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-sm font-mono font-bold">{formatCurrency(q.total_inc_vat)}</p>
                    <p className="text-[10px]" style={{ color: "var(--text-muted)" }}>{q.quote_date}</p>
                    <button onClick={(e) => handleDeleteQuote(e, q.id, q.quote_number)} className="mt-1 text-[10px] font-medium" style={{ color: "var(--danger)" }}>Delete</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="ui-surface overflow-hidden">
            <table className="ui-table">
              <thead>
                <tr>
                  <th>Quote #</th>
                  <th>Date</th>
                  <th>Address</th>
                  <th>Client</th>
                  <th>Type</th>
                  <th className="text-right">Ex VAT</th>
                  <th className="text-right">VAT</th>
                  <th className="text-right">Total</th>
                  <th className="text-center">Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {sorted.map(q => (
                  <tr key={q.id} className="cursor-pointer" onClick={() => navigate(`/quotes/${q.id}`)}>
                    <td className="font-mono font-bold" style={{ color: "var(--primary)" }}>{q.quote_number}</td>
                    <td className="text-xs" style={{ color: "var(--text-secondary)" }}>{q.quote_date || "—"}</td>
                    <td className="font-medium max-w-[200px] truncate">{q.site_address || "—"}</td>
                    <td>
                      <span className="text-sm">{[q.client_first_name, q.client_last_name].filter(Boolean).join(" ") || "—"}</span>
                    </td>
                    <td><span className="badge-pill badge-blue text-[10px]">{q.client_type}</span></td>
                    <td className="text-right font-mono text-sm">{formatCurrency(q.total_ex_vat)}</td>
                    <td className="text-right font-mono text-sm" style={{ color: "var(--text-muted)" }}>{formatCurrency(q.vat_amount)}</td>
                    <td className="text-right font-mono text-sm font-bold">{formatCurrency(q.total_inc_vat)}</td>
                    <td className="text-center">
                      <span className={`badge-pill text-[10px] status-${(q.status || "not-started").toLowerCase().replace(/\s+/g, "-")}`}>{q.status}</span>
                    </td>
                    <td>
                      <div className="flex items-center gap-1">
                        <a href={getQuotePdfUrl(q.id)} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="inline-flex items-center gap-1 px-2 py-1 rounded text-[10px] font-semibold transition-colors" style={{ background: "var(--primary-light)", color: "var(--primary)" }}>
                          <Download className="w-3 h-3" /> PDF
                        </a>
                        <button onClick={(e) => handleDeleteQuote(e, q.id, q.quote_number)} className="p-1 rounded hover:bg-red-50 transition-colors" style={{ color: "var(--text-muted)" }} title="Delete permanently">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}